import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import schedulingApi from '../../api/scheduling';
import usersApi from '../../api/users';

const ROLES = [
  { value: 'ON_CALL', label: 'On Call' },
  { value: 'SITTER',  label: 'Sitter'  },
];

function formatDate(iso) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

// ── Assignment row (existing) ─────────────────────────────────────────────────

function AssignmentRow({ assignment, scheduleStatus, onDelete }) {
  const { sl_detail, role, is_ssl_cover, note } = assignment;
  const name = sl_detail ? `${sl_detail.first_name} ${sl_detail.last_name}` : '—';
  const canEdit = scheduleStatus === 'DRAFT';

  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
        <div className="flex gap-2 mt-0.5">
          <span className={`text-xs font-semibold ${role === 'SITTER' ? 'text-yellow-600' : 'text-indigo-600'}`}>
            {role === 'SITTER' ? 'Sitter' : 'On Call'}
          </span>
          {is_ssl_cover && (
            <span className="text-xs font-semibold text-purple-600">SSL Cover</span>
          )}
          {note && <span className="text-xs text-gray-400 truncate">{note}</span>}
        </div>
      </div>
      {canEdit && (
        <button
          onClick={() => onDelete(assignment.id)}
          className="flex-shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
          title="Remove"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ── Add assignment form ───────────────────────────────────────────────────────

function AddAssignmentForm({ scheduleId, date, districtId, onAdded }) {
  const [slId,        setSlId]        = useState('');
  const [role,        setRole]        = useState('ON_CALL');
  const [isSSLCover,  setIsSSLCover]  = useState(false);
  const [note,        setNote]        = useState('');
  const [error,       setError]       = useState('');

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn:  usersApi.getAll,
  });

  // Only SLs and SSLs from this district are eligible
  const eligible = users.filter(
    (u) => ['SL', 'SSL'].includes(u.role) && u.is_active
  );

  const mutation = useMutation({
    mutationFn: schedulingApi.createShift,
    onSuccess: () => {
      setSlId(''); setRole('ON_CALL'); setIsSSLCover(false); setNote(''); setError('');
      onAdded();
    },
    onError: (err) => setError(err.data ? JSON.stringify(err.data) : err.message),
  });

  const handleAdd = () => {
    if (!slId) { setError('Select an SL first.'); return; }
    mutation.mutate({
      schedule: scheduleId,
      date,
      sl: parseInt(slId, 10),
      role,
      is_ssl_cover: isSSLCover,
      note,
    });
  };

  return (
    <div className="space-y-3 rounded-xl border border-dashed border-indigo-200 bg-indigo-50/30 p-4">
      <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Add Assignment</p>

      {error && (
        <p className="text-xs text-red-600">{error}</p>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">SL</label>
          <select
            value={slId}
            onChange={(e) => setSlId(e.target.value)}
            className={inputCls}
          >
            <option value="">— Select SL —</option>
            {eligible.map((u) => (
              <option key={u.id} value={u.id}>
                {u.first_name} {u.last_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value)} className={inputCls}>
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Note (optional)</label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. covering for injury"
          className={inputCls}
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
        <input
          type="checkbox"
          checked={isSSLCover}
          onChange={(e) => setIsSSLCover(e.target.checked)}
          className="rounded border-gray-300 text-indigo-600"
        />
        SSL emergency cover (does not count toward SL shift totals)
      </label>

      <button
        onClick={handleAdd}
        disabled={mutation.isPending}
        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40 transition-colors"
      >
        {mutation.isPending ? 'Adding…' : '+ Add'}
      </button>
    </div>
  );
}

// ── Modal shell ───────────────────────────────────────────────────────────────

export default function ShiftEditorModal({ date, schedule, onClose }) {
  const queryClient = useQueryClient();

  // Hooks must come before any early returns
  const dateStr     = date ? date.toISOString().slice(0, 10) : null;
  const assignments = dateStr ? (schedule?.assignments ?? []).filter((a) => a.date === dateStr) : [];
  const isEditable  = schedule?.status === 'DRAFT';

  const deleteMutation = useMutation({
    mutationFn: schedulingApi.deleteShift,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['schedule-detail', schedule?.id] }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['schedule-detail', schedule?.id] });

  if (!date || !schedule) return null;

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <p className="font-semibold text-gray-900">{formatDate(dateStr)}</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {schedule.location?.name ?? 'Location'} ·{' '}
              <span className={`font-medium ${isEditable ? 'text-yellow-600' : 'text-green-600'}`}>
                {schedule.status}
              </span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Current assignments */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Assigned ({assignments.length})
            </p>
            {assignments.length ? (
              assignments.map((a) => (
                <AssignmentRow
                  key={a.id}
                  assignment={a}
                  scheduleStatus={schedule.status}
                  onDelete={(id) => deleteMutation.mutate(id)}
                />
              ))
            ) : (
              <p className="text-sm text-gray-400 italic">No SLs assigned yet.</p>
            )}
          </div>

          {/* Add form — only on DRAFT */}
          {isEditable && (
            <AddAssignmentForm
              scheduleId={schedule.id}
              date={dateStr}
              onAdded={invalidate}
            />
          )}

          {!isEditable && (
            <p className="text-xs text-gray-400 text-center">
              This schedule is published — edits are locked.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';
